
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Project, Expense, Photo } from "../types";

// CORES ATUALIZADAS PARA O PDF
const COLORS = {
  primary: "#2DCC95", // Fresh Green
  secondary: "#032221", // Dark Green (Texto forte)
  text: "#5A6363", // Slate 500 equivalent
  lightGray: "#F1F7F6", // White off-white
  white: "#ffffff",
  accent: "#02D181", // Bright Green
  headerBg: "#F0FDF8", // Verde muito claro (Emerald 50) para o header
  brandDark: "#064E3B", // Verde escuro da marca
  brandGreen: "#10B981" // Verde vibrante da marca
};

const formatCurrency = (value: number) => {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

// Helper para carregar imagem local como Base64
const getBase64ImageFromURL = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL("image/png");
          resolve(dataURL);
      } else {
          reject(new Error("Canvas context failed"));
      }
    };
    img.onerror = error => reject(error);
    img.src = url;
  });
};

export const generateProjectReport = async (project: Project, userName: string) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // 1. Carregar Logo do App
  let appLogo: string | null = null;
  try {
    appLogo = await getBase64ImageFromURL('/logo.png');
  } catch (e) {
    console.warn("Logo do app não encontrada ou erro ao carregar", e);
  }

  // Função de Cabeçalho Padrão
  const drawHeader = (sectionTitle: string) => {
      // Barra Verde Muito Claro (Fundo)
      doc.setFillColor(COLORS.headerBg);
      doc.rect(0, 0, pageWidth, 24, 'F');

      // Logo App
      if (appLogo) {
         doc.addImage(appLogo, 'PNG', 14, 4, 16, 16);
      }

      // Texto "MINHA" (Escuro e Bold)
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(COLORS.brandDark);
      doc.text("MINHA", 34, 15); 

      // Texto "OBRA" (Verde Vibrante e Normal - simulando light)
      const minhaWidth = doc.getTextWidth("MINHA");
      doc.setFont("helvetica", "normal");
      doc.setTextColor(COLORS.brandGreen);
      doc.text("OBRA", 34 + minhaWidth + 1, 15);

      // Título da Seção (Alinhado à direita, cor neutra escura para contraste no fundo claro)
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(COLORS.text); 
      doc.text(sectionTitle.toUpperCase(), pageWidth - 14, 15, { align: 'right' });
  };

  let currentY = 0;

  // --- CAPA ---
  // Background Header
  doc.setFillColor(COLORS.secondary);
  doc.rect(0, 0, pageWidth, 297, 'F'); // Full page bg initially for cover style

  // White Box Content
  doc.setFillColor(COLORS.white);
  doc.roundedRect(20, 20, pageWidth - 40, pageHeight - 40, 5, 5, 'F');

  // Logo do PROJETO (Central)
  if (project.logoUrl && project.logoUrl.startsWith('data:image')) {
    try {
      doc.addImage(project.logoUrl, 'PNG', pageWidth / 2 - 25, 40, 50, 50);
    } catch (e) {
      console.warn("Erro ao adicionar logo do projeto:", e);
    }
  }

  // Titles
  doc.setTextColor(COLORS.secondary);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório de Acompanhamento", pageWidth / 2, 110, { align: "center" });
  
  doc.setFontSize(32);
  doc.text(project.name.toUpperCase(), pageWidth / 2, 125, { align: "center" });

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(COLORS.text);
  doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, pageWidth / 2, 140, { align: "center" });
  
  doc.text(`Proprietário: ${userName}`, pageWidth / 2, 250, { align: "center" });
  
  // Footer da Capa com Logo do App Estilizada
  if (appLogo) {
      const footerY = 265;
      doc.addImage(appLogo, 'PNG', pageWidth / 2 - 8, footerY, 16, 16);
      
      // Texto Minha Obra no footer da capa
      doc.setFontSize(12);
      
      doc.setFont("helvetica", "bold");
      doc.setTextColor(COLORS.brandDark);
      doc.text("MINHA", pageWidth / 2 - 8, footerY + 22, { align: "right" });
      
      doc.setFont("helvetica", "normal");
      doc.setTextColor(COLORS.brandGreen);
      doc.text("OBRA", pageWidth / 2 - 6, footerY + 22, { align: "left" });
  }

  // --- PÁGINA 2: RESUMO EXECUTIVO ---
  doc.addPage();
  drawHeader("Resumo Executivo");

  currentY = 35; // Ajustado para não colar no header maior
  
  // Calculos
  const totalSpent = project.expenses.filter(e => e.isPaid).reduce((a, b) => a + b.amount, 0);
  const totalBudget = project.initialBudget || 1;
  const pending = project.expenses.filter(e => !e.isPaid).reduce((a, b) => a + b.amount, 0);
  const progress = (totalSpent / totalBudget);
  const remaining = totalBudget - totalSpent;

  // Cards Visuais
  currentY += 10;
  const cardWidth = (pageWidth - 28 - 20) / 3; // 28 margins, 20 gap
  const cardHeight = 30;

  // Card 1: Orçamento
  doc.setFillColor(COLORS.lightGray);
  doc.roundedRect(14, currentY, cardWidth, cardHeight, 3, 3, 'F');
  doc.setFontSize(8);
  doc.setTextColor(COLORS.text);
  doc.text("ORÇAMENTO TOTAL", 19, currentY + 8);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.secondary);
  doc.text(formatCurrency(totalBudget), 19, currentY + 20);

  // Card 2: Executado
  doc.setFillColor(COLORS.lightGray);
  doc.roundedRect(14 + cardWidth + 10, currentY, cardWidth, cardHeight, 3, 3, 'F');
  doc.setFontSize(8);
  doc.setTextColor(COLORS.text);
  doc.setFont("helvetica", "normal");
  doc.text("TOTAL EXECUTADO", 19 + cardWidth + 10, currentY + 8);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.primary);
  doc.text(formatCurrency(totalSpent), 19 + cardWidth + 10, currentY + 20);

  // Card 3: Saldo
  doc.setFillColor(COLORS.lightGray);
  doc.roundedRect(14 + (cardWidth + 10) * 2, currentY, cardWidth, cardHeight, 3, 3, 'F');
  doc.setFontSize(8);
  doc.setTextColor(COLORS.text);
  doc.setFont("helvetica", "normal");
  doc.text("SALDO DISPONÍVEL", 19 + (cardWidth + 10) * 2, currentY + 8);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(remaining >= 0 ? COLORS.secondary : "#EF4444"); // Red if negative
  doc.text(formatCurrency(remaining), 19 + (cardWidth + 10) * 2, currentY + 20);

  // Barra de Progresso
  currentY += 45;
  doc.setFontSize(10);
  doc.setTextColor(COLORS.text);
  doc.text(`Progresso Financeiro: ${(progress * 100).toFixed(1)}%`, 14, currentY - 5);
  
  // Fundo Barra
  doc.setFillColor("#e2e8f0");
  doc.roundedRect(14, currentY, pageWidth - 28, 6, 3, 3, 'F');
  
  // Fill Barra
  doc.setFillColor(COLORS.primary);
  const fillWidth = Math.min(progress, 1) * (pageWidth - 28);
  doc.roundedRect(14, currentY, fillWidth, 6, 3, 3, 'F');

  // --- TABELA DE FLUXO (PRÓXIMOS GASTOS) ---
  currentY += 20;
  doc.setFontSize(14);
  doc.setTextColor(COLORS.secondary);
  doc.text("Contas a Pagar (Próximos 30 dias)", 14, currentY);

  const today = new Date();
  const nextMonth = new Date();
  nextMonth.setDate(today.getDate() + 30);

  const upcomingExpenses = project.expenses
    .filter(e => !e.isPaid && new Date(e.paymentDate) >= today && new Date(e.paymentDate) <= nextMonth)
    .sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime());

  const upcomingRows = upcomingExpenses.map(e => [
    formatDate(e.paymentDate),
    e.description,
    e.supplier || '-',
    formatCurrency(e.amount)
  ]);

  if (upcomingRows.length > 0) {
    autoTable(doc, {
      startY: currentY + 5,
      head: [['Vencimento', 'Descrição', 'Fornecedor', 'Valor']],
      body: upcomingRows,
      theme: 'grid',
      headStyles: { fillColor: COLORS.secondary, textColor: COLORS.white },
      styles: { fontSize: 9 },
      margin: { top: 25 },
      didDrawPage: (data) => {
          if (data.pageNumber > 2) {
              drawHeader("Fluxo de Caixa");
          }
      }
    });
    // @ts-ignore
    currentY = doc.lastAutoTable.finalY + 20;
  } else {
    doc.setFontSize(10);
    doc.setTextColor(COLORS.text);
    doc.text("Nenhuma conta prevista para os próximos 30 dias.", 14, currentY + 10);
    currentY += 25;
  }

  // --- EXTRATO POR ETAPA ---
  // Verifica se precisa de nova página antes de começar a tabela
  if (currentY > pageHeight - 60) {
      doc.addPage();
      drawHeader("Relatório Financeiro");
      currentY = 35;
  }

  doc.setFontSize(14);
  doc.setTextColor(COLORS.secondary);
  doc.text("Gastos por Etapa", 14, currentY);

  const stageRows = project.stages.map(stage => {
    const spent = project.expenses
      .filter(e => e.stageId === stage.id && e.isPaid)
      .reduce((a, b) => a + b.amount, 0);
    return [stage.name, formatCurrency(spent)];
  });

  autoTable(doc, {
    startY: currentY + 5,
    head: [['Etapa', 'Total Gasto']],
    body: stageRows,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white },
    styles: { fontSize: 10 },
    margin: { top: 25 },
    didDrawPage: (data) => {
        // Se a tabela quebrar página, adiciona header
        if (data.cursor.y < 30) { 
            drawHeader("Detalhamento Financeiro");
        }
    }
  });

  // --- PÁGINA 3+: GALERIA DE FOTOS ---
  if (project.photos.length > 0) {
    doc.addPage();
    drawHeader("Diário Visual");

    doc.setTextColor(COLORS.secondary);
    doc.setFontSize(14);
    doc.text("Destaques da Obra", 14, 35);

    let photoY = 45;
    let col = 0;
    const photoWidth = 80;
    const photoHeight = 60;
    const gap = 15;

    // Pegar as ultimas 6 fotos
    const recentPhotos = [...project.photos]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 6);

    for (const photo of recentPhotos) {
      if (photo.url && photo.url.startsWith('data:image')) {
        const xPos = 14 + (col * (photoWidth + gap));
        
        try {
          doc.addImage(photo.url, 'JPEG', xPos, photoY, photoWidth, photoHeight);
          
          // Caption box
          doc.setFillColor(COLORS.lightGray);
          doc.rect(xPos, photoY + photoHeight, photoWidth, 15, 'F');
          
          doc.setFontSize(8);
          doc.setTextColor(COLORS.secondary);
          doc.setFont("helvetica", "bold");
          doc.text(formatDate(photo.date) + " - " + photo.room, xPos + 2, photoY + photoHeight + 5);
          
          doc.setFont("helvetica", "normal");
          doc.setTextColor(COLORS.text);
          const desc = doc.splitTextToSize(photo.description || "", photoWidth - 4);
          doc.text(desc, xPos + 2, photoY + photoHeight + 10);

        } catch (e) {
          console.warn("Erro imagem PDF", e);
        }

        col++;
        if (col > 1) {
          col = 0;
          photoY += photoHeight + gap + 20;
        }
        
        // Nova página se necessário
        if (photoY > pageHeight - 60) {
           doc.addPage();
           drawHeader("Diário Visual (Cont.)");
           photoY = 35; // Reset Y com espaço para o header
        }
      }
    }
  }

  // --- RODAPÉ EM TODAS AS PAGINAS (exceto capa) ---
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 2; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: "right" });
  }

  doc.save(`Relatorio_${project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
};
